import { useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import imageCompression from "browser-image-compression";

const ALLOWED_TYPES = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/heic",
    "application/pdf",
];

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB pre-compression (images get compressed down to ~1MB)
const MAX_PDF_SIZE = 25 * 1024 * 1024;   // 25MB for PDFs/documents (floor plans can be large)

// Signed URLs stored directly in the DB (receipt_url, invoice_url, warranty_url)
// need a long expiry since nothing currently re-signs them before display —
// a 7-day expiry meant every uploaded document went dead a week after upload.
// 10 years is effectively "doesn't expire in practice" for a private bucket.
const STORED_URL_EXPIRY_SECONDS = 60 * 60 * 24 * 365 * 10;

export const useFileUpload = () => {
    const { user } = useAuth();
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);

    const validateFile = useCallback((file) => {
        if (!ALLOWED_TYPES.includes(file.type)) {
            toast({
                title: "Invalid File Type",
                description: "Only images (JPEG, PNG, GIF, WebP) and PDFs are allowed.",
                variant: "destructive",
            });
            return false;
        }

        const isImage = file.type.startsWith("image/");
        const limit = isImage ? MAX_IMAGE_SIZE : MAX_PDF_SIZE;
        const limitLabel = isImage ? "10MB" : "25MB";

        if (file.size > limit) {
            toast({
                title: "File Too Large",
                description: `${isImage ? "Images" : "Documents"} must be under ${limitLabel}.`,
                variant: "destructive",
            });
            return false;
        }

        return true;
    }, []);

    const compressImage = async (file) => {
        // Only compress images
        if (!file.type.startsWith("image/")) return file;

        const options = {
            maxSizeMB: 0.8, // Aim for under 1MB
            maxWidthOrHeight: 1920, // Full HD max
            useWebWorker: true,
            fileType: "image/webp", // Convert to WebP for better compression
        };

        try {
            const compressedFile = await imageCompression(file, options);
            // Return as a fresh file object with a proper name and type
            return new File([compressedFile], file.name.replace(/\.[^/.]+$/, ".webp"), {
                type: "image/webp",
                lastModified: Date.now(),
            });
        } catch (error) {
            console.error("Compression error:", error);
            return file; // Fallback to original if compression fails
        }
    };

    const uploadFile = useCallback(async (
        file,
        folder = "documents",
        bucketName = "service-documents"
    ) => {
        if (!user) {
            toast({
                title: "Authentication Required",
                description: "You must be logged in to upload files.",
                variant: "destructive",
            });
            return null;
        }

        if (!validateFile(file)) return null;

        setUploading(true);
        setProgress(0);

        try {
            // Compress if it's an image
            const fileToUpload = file.type.startsWith("image/")
                ? await compressImage(file)
                : file;

            const fileExt = fileToUpload.name.split(".").pop()?.toLowerCase() || "bin";
            const timestamp = Date.now();
            const randomId = Math.random().toString(36).substring(2, 9);
            const fileName = `${timestamp}-${randomId}.${fileExt}`;
            const filePath = `${user.id}/${folder}/${fileName}`;

            // Upload to Supabase Storage
            const { error: uploadError } = await supabase.storage
                .from(bucketName)
                .upload(filePath, fileToUpload, {
                    cacheControl: "3600",
                    upsert: false,
                });

            if (uploadError) {
                throw new Error(uploadError.message);
            }

            // Get signed URL. Long-lived because the stored URL is what's saved to
            // the DB and rendered directly (receipt_url/invoice_url/warranty_url) —
            // nothing currently re-signs it before display.
            const { data: signedUrlData, error: signedUrlError } = await supabase.storage
                .from(bucketName)
                .createSignedUrl(filePath, STORED_URL_EXPIRY_SECONDS);

            if (signedUrlError) {
                throw new Error("Failed to generate file URL");
            }

            setProgress(100);

            return {
                url: signedUrlData.signedUrl,
                path: filePath,
                name: fileToUpload.name,
                type: fileToUpload.type,
                size: fileToUpload.size,
                bucket: bucketName
            };
        } catch (error) {
            console.error("Upload error:", error);
            const message = error instanceof Error ? error.message : "Failed to upload file";
            toast({
                title: "Upload Failed",
                description: message,
                variant: "destructive",
            });
            return null;
        } finally {
            setUploading(false);
            setProgress(0);
        }
    }, [user, validateFile]);

    // Trigger native camera capture via file input
    const capturePhoto = useCallback(() => {
        return new Promise((resolve) => {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = "image/*";
            input.capture = "environment"; // Use back camera on mobile

            input.onchange = (e) => {
                const target = e.target;
                const file = target.files?.[0] || null;
                resolve(file);
            };

            input.oncancel = () => resolve(null);

            input.click();
        });
    }, []);

    // Trigger native file picker
    const selectFile = useCallback((accept = "image/jpeg,image/png,image/gif,image/webp,application/pdf") => {
        return new Promise((resolve) => {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = accept;

            input.onchange = (e) => {
                const target = e.target;
                const file = target.files?.[0] || null;
                resolve(file);
            };

            input.oncancel = () => resolve(null);

            input.click();
        });
    }, []);

    // Request camera permission explicitly (for video capture use cases)
    const requestCameraPermission = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "environment" },
            });
            // Stop the stream immediately - we just needed to request permission
            stream.getTracks().forEach(track => track.stop());
            return true;
        } catch (error) {
            console.error("Camera permission denied:", error);
            toast({
                title: "Camera Access Denied",
                description: "Please allow camera access in your browser settings.",
                variant: "destructive",
            });
            return false;
        }
    }, []);

    // Delete a file from storage
    const deleteFile = useCallback(async (filePath, bucketName = "service-documents") => {
        if (!user || !filePath) return false;

        try {
            const { error } = await supabase.storage
                .from(bucketName)
                .remove([filePath]);

            if (error) {
                throw error;
            }

            return true;
        } catch (error) {
            console.error("Delete error:", error);
            toast({
                title: "Delete Failed",
                description: "Failed to delete the file.",
                variant: "destructive",
            });
            return false;
        }
    }, [user]);

    // Get fresh signed URL for existing stored file
    const getFreshSignedUrl = useCallback(async (filePath, bucketName = "service-documents", expiresInSeconds = STORED_URL_EXPIRY_SECONDS) => {
        if (!filePath) return null;
        try {
            const { data, error } = await supabase.storage
                .from(bucketName)
                .createSignedUrl(filePath, expiresInSeconds);
            if (error) throw error;
            return data?.signedUrl || null;
        } catch (err) {
            console.error("Error generating signed URL:", err);
            return null;
        }
    }, []);

    return {
        uploading,
        progress,
        uploadFile,
        capturePhoto,
        selectFile,
        requestCameraPermission,
        validateFile,
        compressImage,
        deleteFile,
        getFreshSignedUrl,
    };
};
